import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiMapPin, FiPhone, FiMail } = FiIcons;

const ServiceCard = ({ service }) => {
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <SafeIcon key="half" icon={FiStar} className="text-yellow-400 fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <SafeIcon key={`empty-${i}`} icon={FiStar} className="text-gray-300" />
      );
    }

    return stars;
  };

  const getPriceDisplay = (price) => {
    const priceMap = {
      '$': 'Budget-friendly',
      '$$': 'Moderate',
      '$$$': 'Premium',
      '$$$$': 'Luxury'
    };
    return priceMap[price] || price;
  };

  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
    >
      <div className="relative">
        <img
          src={service.image}
          alt={service.name}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-4 right-4 bg-white px-2 py-1 rounded-full text-sm font-medium">
          {getPriceDisplay(service.price)}
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-semibold text-gray-900 line-clamp-1">
            {service.name}
          </h3>
          <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
            {service.category}
          </span>
        </div>

        <div className="flex items-center mb-2">
          <div className="flex items-center mr-2">
            {renderStars(service.rating)}
          </div>
          <span className="text-sm font-medium text-gray-900">
            {service.rating}
          </span>
          <span className="text-sm text-gray-500 ml-1">
            ({service.reviewCount} reviews)
          </span>
        </div>

        <div className="flex items-center text-gray-600 mb-3">
          <SafeIcon icon={FiMapPin} className="mr-1 text-sm" />
          <span className="text-sm">{service.location}</span>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {service.description}
        </p>

        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {service.services.slice(0, 3).map((serviceItem, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
              >
                {serviceItem}
              </span>
            ))}
            {service.services.length > 3 && (
              <span className="text-xs text-gray-500 px-2 py-1">
                +{service.services.length - 3} more
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <a
              href={`tel:${service.phone}`}
              className="text-primary-600 hover:text-primary-700 transition-colors"
            >
              <SafeIcon icon={FiPhone} className="text-lg" />
            </a>
            <a
              href={`mailto:${service.email}`}
              className="text-primary-600 hover:text-primary-700 transition-colors"
            >
              <SafeIcon icon={FiMail} className="text-lg" />
            </a>
          </div>
          <Link
            to={`/provider/${service.id}`}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            View Profile
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default ServiceCard;